import { CSSProperties, forwardRef, memo } from "react";
import Icon, { IconNames } from "./Icon.tsx";
import React from "react";
import LoadingComponent from "../../components/LoadingComponent/LoadingComponent.tsx";
import { SPACINGS } from "../spacings.ts";
import { motion } from "framer-motion";

type IconButtonProps = {
	icon: IconNames;
	size?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
	theme?: "light" | "dark";
	disabled?: boolean;
	loading?: boolean;
	backgroundColor?: string;
	hoverColor?: string;
	borderColor?: string;
	onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
};
const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
	(
		{
			size = "sm",
			theme = "light",
			disabled = false,
			icon,
			onClick,
			loading,
			backgroundColor = "#e4e4e4",
			hoverColor = undefined,
			borderColor = undefined,
			...rest
		},
		ref
	) => {
		const iconSize = ["xs", "sm", "md", "lg", "xl", "2xl"].includes(size)
			? size
			: "md";

		if (!hoverColor) {
			hoverColor = theme === "dark" ? "#e0e0e0" : "#222222";
		}

		const style: CSSProperties = {
			padding: SPACINGS[size] / 3,
			minHeight: "fit-content",
			minWidth: "fit-content",
			borderRadius: "100%",
			border: borderColor ? `1px solid ${borderColor}` : "none",
			cursor: disabled || loading ? "default" : "pointer",
			outline: "none",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			backgroundColor: disabled || loading ? hoverColor : backgroundColor,
		};

		return (
			<motion.button
				initial={{ backgroundColor }}
				whileHover={{
					backgroundColor: disabled || loading ? backgroundColor : hoverColor,
					scale: disabled || loading ? 1 : 1.05,
				}}
				transition={{ duration: 0.1 }}
				whileTap={{ scale: disabled || loading ? 1 : 0.95 }}
				animate={{
					opacity: disabled || loading ? 0.5 : 1,
				}}
				disabled={disabled || loading}
				onClick={onClick}
				style={style}
				ref={ref}
				{...rest}
			>
				{loading ? (
					<LoadingComponent />
				) : (
					<Icon
						size={iconSize}
						name={icon}
						theme={theme}
						strokeWidth={1}
						{...rest}
					/>
				)}
			</motion.button>
		);
	}
);

export default memo(IconButton);
